import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/EngagementCampaignReport.graphql';
import { Query, QueryengagementCampaignReportArgs } from '../../__generated__/globalTypes';

export const useEngagementCampaignReportQuery = (
  options?: QueryHookOptions<Query, QueryengagementCampaignReportArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryengagementCampaignReportArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useEngagementCampaignReportLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryengagementCampaignReportArgs>
): QueryResult<Query, QueryengagementCampaignReportArgs> & {
  getEngagementCampaignReport: LazyQueryExecFunction<Query, QueryengagementCampaignReportArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getEngagementCampaignReport, result] = useLazyQuery<Query, QueryengagementCampaignReportArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getEngagementCampaignReport, ...result };
};

export const useEngagementCampaignReportPromiseQuery = () => {
  const client = useApolloClient();

  const getEngagementCampaignReport = useCallback(
    (options?: Omit<QueryOptions<QueryengagementCampaignReportArgs, Query>, 'query'>) =>
      client.query<Query, QueryengagementCampaignReportArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getEngagementCampaignReport };
};
// Suspense Query Hooks
export const useEngagementCampaignReportSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryengagementCampaignReportArgs>
) => {
  const result = useSuspenseQuery<Query, QueryengagementCampaignReportArgs>(QUERY, {
    ...options
  });

  return result;
};
